import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Loader2, BookOpen, Filter, Search,
  CheckCircle2, XCircle, AlertTriangle, MinusCircle,
  ChevronLeft, ChevronRight, Download, RefreshCw,
  CalendarDays, Table2,
} from "lucide-react";

import { LOGO_URL } from "../config/constants";

const LABEL = { yes: "Yes", no: "No", na: "N/A", drafted: "Drafted", requested: "Requested", invoiced: "Invoiced", "": "—" };

function StatusCell({ value }) {
  const v = (value || "").toLowerCase();
  if (v === "yes") return <span className="text-teal font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Yes</span>;
  if (v === "no") return <span className="text-status-red font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> No</span>;
  if (v === "drafted") return <span className="text-status-amber font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Drafted</span>;
  if (v === "requested") return <span className="text-blue-400 font-medium flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Requested</span>;
  if (v === "na" || v === "n/a") return <span className="text-txt-muted flex items-center gap-1"><MinusCircle className="w-3 h-3" /> N/A</span>;
  return <span className="text-txt-muted">{value || "—"}</span>;
}

function IssuesFlag({ entry }) {
  const fields = ["signed_docs_received", "os_and_k_searches_done", "exchanged_flag", "redemption_statement_received", "completion_statement_prepared", "cot_submitted", "client_funds_requested"];
  const issues = fields.filter(f => entry[f] === "no").length;
  if (issues === 0) return <span className="text-teal text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal/10 border border-teal/20">OK</span>;
  return <span className="text-status-red text-[10px] font-bold px-1.5 py-0.5 rounded bg-status-red/10 border border-red-500/20">{issues} issue{issues !== 1 ? "s" : ""}</span>;
}

// Date range helpers
function getToday() { return new Date().toISOString().split("T")[0]; }
function getWeekStart() { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); return d.toISOString().split("T")[0]; }
function getMonthStart() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; }
function getYearStart() { return `${new Date().getFullYear()}-01-01`; }

const RANGES = [
  { key: "today", label: "Today", from: getToday, to: getToday },
  { key: "week", label: "This Week", from: getWeekStart, to: () => "2099-12-31" },
  { key: "month", label: "This Month", from: getMonthStart, to: () => "2099-12-31" },
  { key: "year", label: "This Year", from: getYearStart, to: () => "2099-12-31" },
  { key: "all", label: "All Time", from: () => "", to: () => "" },
  { key: "custom", label: "Custom", from: () => "", to: () => "" },
];

export default function CompletionsBookDashboard() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const [viewMode, setViewMode] = useState("table"); // "table" or "calendar"
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Filters
  const [rangeKey, setRangeKey] = useState("year");
  const [dateFrom, setDateFrom] = useState(getYearStart());
  const [dateTo, setDateTo] = useState("");
  const [feeEarner, setFeeEarner] = useState("");
  const [txType, setTxType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchEntries();
    fetchCalendar();
  }, [dateFrom, dateTo, feeEarner, txType, statusFilter, issuesOnly, page]);

  const fetchCalendar = async () => {
    setCalendarLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo && dateTo !== "2099-12-31") params.date_to = dateTo;
      if (feeEarner) params.fee_earner = feeEarner;
      const res = await axios.get(`${API}/completionsbook/calendar`, { params });
      setCalendarData(res.data.days || []);
    } catch { /* ignore */ }
    setCalendarLoading(false);
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: page * pageSize };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo && dateTo !== "2099-12-31") params.date_to = dateTo;
      if (feeEarner) params.fee_earner = feeEarner;
      if (txType) params.transaction_type = txType;
      if (statusFilter) params.status = statusFilter;
      if (issuesOnly) params.issues_only = true;

      const res = await axios.get(`${API}/completionsbook`, { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch { toast.error("Failed to load entries"); }
    setLoading(false);
  };

  const applyRange = (key) => {
    setRangeKey(key);
    const range = RANGES.find(r => r.key === key);
    if (range && key !== "custom") {
      setDateFrom(range.from());
      setDateTo(range.to());
    }
    setPage(0);
  };

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(e =>
      (e.client_name || "").toLowerCase().includes(q) ||
      (e.property_address || "").toLowerCase().includes(q) ||
      (e.matterref || "").toLowerCase().includes(q) ||
      (e.fee_earner || "").toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  const totalPages = Math.ceil(total / pageSize);

  const handleExportCsv = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo && dateTo !== "2099-12-31") params.set("date_to", dateTo);
    if (feeEarner) params.set("fee_earner", feeEarner);
    if (txType) params.set("transaction_type", txType);
    if (statusFilter) params.set("status", statusFilter);
    if (issuesOnly) params.set("issues_only", "true");
    window.open(`${API}/completionsbook/export/csv?${params.toString()}`, "_blank");
  };

  const handleCalendarDayClick = (date) => {
    setRangeKey("custom");
    setDateFrom(date);
    setDateTo(date);
    setPage(0);
    setViewMode("table");
  };

  // Stats
  const stats = useMemo(() => {
    const e = entries;
    return {
      total: e.length,
      completed: e.filter(x => x.completed_flag === "yes").length,
      issues: e.filter(x => ["signed_docs_received","os_and_k_searches_done","exchanged_flag","redemption_statement_received","completion_statement_prepared","cot_submitted","client_funds_requested"].some(f => x[f] === "no")).length,
      fundsRequested: e.filter(x => x.client_funds_requested === "requested").length,
    };
  }, [entries]);

  return (
    <div className="min-h-screen bg-navy">
      <nav className="sticky top-0 z-30 glass-nav border-b border-[rgba(99,102,241,0.25)]">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai" className="h-7" />
            <span className="text-white font-bold text-sm">Completions Book — Residential</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-navy-border rounded-md overflow-hidden mr-1">
              <button onClick={() => setViewMode("table")} className={`px-2.5 py-1.5 text-xs font-medium ${viewMode === "table" ? "bg-teal text-white" : "text-txt-muted hover:text-white"}`} data-testid="cb-view-table"><Table2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => setViewMode("calendar")} className={`px-2.5 py-1.5 text-xs font-medium ${viewMode === "calendar" ? "bg-teal text-white" : "text-txt-muted hover:text-white"}`} data-testid="cb-view-calendar"><CalendarDays className="w-3.5 h-3.5" /></button>
            </div>
            <Button variant="outline" onClick={handleExportCsv} className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white gap-1.5" data-testid="cb-export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </Button>
            <Button onClick={() => navigate("/completions-book/new")} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="cb-new-entry-btn">
              <Plus className="w-4 h-4" /> New Entry
            </Button>
            <Button variant="ghost" onClick={() => navigate("/")} className="text-txt-muted hover:text-teal" data-testid="cb-back-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[1400px] mx-auto px-6 py-6 animate-fade-in-up">
        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[
            { label: "Entries", val: stats.total, cls: "text-white" },
            { label: "Completed", val: stats.completed, cls: "text-teal" },
            { label: "With Issues", val: stats.issues, cls: stats.issues > 0 ? "text-status-red" : "text-txt-muted" },
            { label: "Funds Requested", val: stats.fundsRequested, cls: stats.fundsRequested > 0 ? "text-blue-400" : "text-txt-muted" },
          ].map(s => (
            <div key={s.label} className="glass-card p-3 text-center">
              <p className={`text-xl font-bold ${s.cls}`}>{s.val}</p>
              <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-4" data-testid="cb-filters">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <Filter className="w-4 h-4 text-teal" />
            <span className="text-xs font-bold text-txt uppercase tracking-wider mr-2">Range:</span>
            {RANGES.map(r => (
              <button key={r.key} onClick={() => applyRange(r.key)} className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${rangeKey === r.key ? "bg-teal border-teal/50 text-white" : "border-navy-border text-txt-muted hover:text-white"}`} data-testid={`cb-range-${r.key}`}>
                {r.label}
              </button>
            ))}
            <button onClick={() => setIssuesOnly(!issuesOnly)} className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ml-auto ${issuesOnly ? "bg-status-red/20 border-red-500/40 text-status-red" : "border-navy-border text-txt-muted hover:text-white"}`} data-testid="cb-issues-only">
              {issuesOnly ? "Showing Issues Only" : "Issues Only"}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            {rangeKey === "custom" && (
              <>
                <div>
                  <span className="text-[10px] text-txt-muted block mb-1">From</span>
                  <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white text-xs w-36" data-testid="cb-date-from" />
                </div>
                <div>
                  <span className="text-[10px] text-txt-muted block mb-1">To</span>
                  <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white text-xs w-36" data-testid="cb-date-to" />
                </div>
              </>
            )}
            <div>
              <span className="text-[10px] text-txt-muted block mb-1">Fee Earner</span>
              <Select value={feeEarner || "all"} onValueChange={v => { setFeeEarner(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white text-xs w-44" data-testid="cb-filter-fe"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Fee Earners</SelectItem>
                  <SelectItem value="RM">Rebekah Brake Manning</SelectItem>
                  <SelectItem value="EH">Emily Howard</SelectItem>
                  <SelectItem value="JD">Jodie Daffin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <span className="text-[10px] text-txt-muted block mb-1">Type</span>
              <Select value={txType || "all"} onValueChange={v => { setTxType(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white text-xs w-36" data-testid="cb-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Sale">Sale</SelectItem>
                  <SelectItem value="Purchase">Purchase</SelectItem>
                  <SelectItem value="Remortgage">Remortgage</SelectItem>
                  <SelectItem value="ToE Remo">ToE Remo</SelectItem>
                  <SelectItem value="H2B Remo">H2B Remo</SelectItem>
                  <SelectItem value="Bridging ReFinance">Bridging ReFinance</SelectItem>
                  <SelectItem value="Auction Purchase">Auction Purchase</SelectItem>
                  <SelectItem value="Staircasing">Staircasing</SelectItem>
                  <SelectItem value="RTB Purchase">RTB Purchase</SelectItem>
                  <SelectItem value="Acting for lender">Acting for lender</SelectItem>
                  <SelectItem value="Redemption">Redemption</SelectItem>
                  <SelectItem value="Transfer">Transfer</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <span className="text-[10px] text-txt-muted block mb-1">Status</span>
              <Select value={statusFilter || "all"} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white text-xs w-36" data-testid="cb-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Invoiced">Invoiced</SelectItem>
                  <SelectItem value="Awaiting invoice">Awaiting invoice</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <span className="text-[10px] text-txt-muted block mb-1">Search</span>
              <div className="relative">
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Client, property, matter ref..." className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white text-xs placeholder:text-txt-muted pl-7" data-testid="cb-search" />
                <Search className="w-3.5 h-3.5 text-txt-muted absolute left-2 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Heatmap or Table */}
        {viewMode === "calendar" ? (
          <CalendarHeatmap data={calendarData} loading={calendarLoading} onDayClick={handleCalendarDayClick} />
        ) : (
        <div className="glass-card overflow-hidden" data-testid="cb-table-container">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cb-table">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-txt-muted border-b border-navy-border bg-navy-card">
                  <th className="p-2.5 sticky left-0 bg-navy-card z-10 min-w-[85px]">Date</th>
                  <th className="p-2.5 min-w-[40px]">FE</th>
                  <th className="p-2.5 min-w-[140px]">Client</th>
                  <th className="p-2.5 min-w-[140px]">Property</th>
                  <th className="p-2.5 min-w-[70px]">Type</th>
                  <th className="p-2.5 min-w-[90px]">Value</th>
                  <th className="p-2.5 text-center min-w-[55px]">Docs</th>
                  <th className="p-2.5 text-center min-w-[55px]">OS1/K</th>
                  <th className="p-2.5 text-center min-w-[55px]">Exch</th>
                  <th className="p-2.5 text-center min-w-[55px]">Redem</th>
                  <th className="p-2.5 text-center min-w-[55px]">C.Stmt</th>
                  <th className="p-2.5 text-center min-w-[55px]">CoT</th>
                  <th className="p-2.5 text-center min-w-[65px]">Funds</th>
                  <th className="p-2.5 min-w-[70px]">Status</th>
                  <th className="p-2.5 text-center min-w-[45px]">Flag</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={14} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-teal mx-auto" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={14} className="p-8 text-center text-txt-muted">No entries found for the selected filters.</td></tr>
                ) : (
                  filtered.map((e) => (
                    <tr
                      key={e.id}
                      onClick={() => navigate(`/completions-book/${e.id}`)}
                      className="border-b border-navy-border/30 hover:bg-navy-hover/30 cursor-pointer transition-colors"
                      data-testid={`cb-row-${e.id}`}
                    >
                      <td className="p-2.5 sticky left-0 bg-navy font-semibold text-white whitespace-nowrap">{e.completion_date}</td>
                      <td className="p-2.5 text-white font-medium">{e.fee_earner}</td>
                      <td className="p-2.5 text-white truncate max-w-[180px]">{e.client_name}</td>
                      <td className="p-2.5 text-txt truncate max-w-[180px]">{e.property_address || "—"}</td>
                      <td className="p-2.5 text-txt">{e.transaction_type}</td>
                      <td className="p-2.5 text-teal font-mono text-xs">{e.transaction_value || "—"}</td>
                      <td className="p-2.5 text-center"><StatusCell value={e.signed_docs_received} /></td>
                      <td className="p-2.5 text-center"><StatusCell value={e.os_and_k_searches_done} /></td>
                      <td className="p-2.5 text-center"><StatusCell value={e.exchanged_flag} /></td>
                      <td className="p-2.5 text-center"><StatusCell value={e.redemption_statement_received} /></td>
                      <td className="p-2.5 text-center"><StatusCell value={e.completion_statement_prepared} /></td>
                      <td className="p-2.5 text-center"><StatusCell value={e.cot_submitted} /></td>
                      <td className="p-2.5 text-center"><StatusCell value={e.client_funds_requested} /></td>
                      <td className="p-2.5 text-txt">{e.status || "—"}</td>
                      <td className="p-2.5 text-center"><IssuesFlag entry={e} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-navy-border">
              <span className="text-xs text-txt-muted">
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
              </span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="text-txt-muted">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="text-txt-muted">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
        )}
      </main>
    </div>
  );
}

// ── Calendar Heatmap Component ──
function CalendarHeatmap({ data, loading, onDayClick }) {
  if (loading) {
    return <div className="glass-card p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-teal mx-auto" /></div>;
  }

  // Build a lookup map
  const dayMap = {};
  let maxCount = 0;
  data.forEach(d => {
    dayMap[d.date] = d;
    if (d.count > maxCount) maxCount = d.count;
  });

  // Generate calendar months from the data range
  const dates = data.map(d => d.date).sort();
  if (dates.length === 0) {
    return <div className="glass-card p-8 text-center text-txt-muted">No data for calendar view.</div>;
  }

  const startDate = new Date(dates[0] + "T00:00:00");
  const endDate = new Date(dates[dates.length - 1] + "T00:00:00");

  // Generate months between start and end
  const months = [];
  const cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  while (cur <= endDate) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }

  const getIntensity = (count) => {
    if (!count || count === 0) return "bg-navy-hover";
    const ratio = count / Math.max(maxCount, 1);
    if (ratio <= 0.25) return "bg-teal-dim/60";
    if (ratio <= 0.5) return "bg-teal/70";
    if (ratio <= 0.75) return "bg-teal/80";
    return "bg-teal";
  };

  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div className="glass-card p-5" data-testid="cb-calendar-heatmap">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-teal" />
          <span className="text-sm font-bold text-white">Completions Calendar</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-txt-muted">
          <span>Less</span>
          <div className="flex gap-0.5">
            {["bg-navy-hover", "bg-teal-dim/60", "bg-teal/70", "bg-teal/80", "bg-teal"].map((cls, i) => (
              <div key={i} className={`w-3 h-3 rounded-sm ${cls}`} />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="flex gap-4">
          {months.map(monthDate => {
            const year = monthDate.getFullYear();
            const month = monthDate.getMonth();
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            const startDow = (firstDay.getDay() + 6) % 7; // Mon=0

            const cells = [];
            // Empty cells before first day
            for (let i = 0; i < startDow; i++) cells.push(null);
            // Day cells
            for (let d = 1; d <= lastDay.getDate(); d++) {
              const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              cells.push({ date: dateStr, day: d, data: dayMap[dateStr] || null });
            }

            // Pad to complete the last week
            while (cells.length % 7 !== 0) cells.push(null);

            const weeks = [];
            for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

            return (
              <div key={`${year}-${month}`} className="flex-shrink-0">
                <p className="text-[10px] font-bold text-txt-muted mb-1.5">{MONTHS_SHORT[month]} {year}</p>
                <div className="flex gap-0.5 mb-1">
                  {DAYS.map(d => <span key={d} className="w-5 text-center text-[8px] text-txt-muted">{d[0]}</span>)}
                </div>
                {weeks.map((week, wi) => (
                  <div key={wi} className="flex gap-0.5 mb-0.5">
                    {week.map((cell, ci) => {
                      if (!cell) return <div key={ci} className="w-5 h-5" />;
                      const count = cell.data?.count || 0;
                      const isWeekend = ci >= 5;
                      return (
                        <button
                          key={ci}
                          onClick={() => count > 0 && onDayClick(cell.date)}
                          className={`w-5 h-5 rounded-sm text-[7px] font-medium flex items-center justify-center transition-colors ${
                            count > 0 ? `${getIntensity(count)} text-white cursor-pointer hover:ring-1 hover:ring-teal` : isWeekend ? "bg-navy-card text-txt-faint" : "bg-navy-hover text-txt-faint"
                          }`}
                          title={count > 0 ? `${cell.date}: ${count} completion${count !== 1 ? "s" : ""} (${cell.data.fee_earners?.join(", ")})` : cell.date}
                          data-testid={`cal-day-${cell.date}`}
                        >
                          {count > 0 ? count : ""}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
